import React, { useEffect, useState } from 'react';
import { Card, Table, Spinner, Form, Row, Col, Modal, Button, Badge } from 'react-bootstrap';
import { chatAPI } from '../api';

const TradersPage = () => {
  const [traders, setTraders] = useState([]);
  const [filteredTraders, setFilteredTraders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('name');
  const [searchTerm, setSearchTerm] = useState('');
  const [styleFilter, setStyleFilter] = useState('전체');
  const [riskFilter, setRiskFilter] = useState('전체');
  const [selectedTrader, setSelectedTrader] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchTraders();
  }, []);

  useEffect(() => {
    filterAndSort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [traders, sortBy, searchTerm, styleFilter, riskFilter]);

  const fetchTraders = async () => {
    try {
      const response = await chatAPI.getTraders();
      console.log('Traders API Response:', response.data);
      
      const tradersData = Array.isArray(response.data) ? response.data : [];
      setTraders(tradersData);
      setFilteredTraders(tradersData);
    } catch (error) {
      console.error('Fetch error:', error);
      setTraders([]);
      setFilteredTraders([]);
    } finally {
      setLoading(false);
    }
  };

  const filterAndSort = () => {
    let result = [...traders];

    // 검색 필터
    if (searchTerm) {
      result = result.filter(t => 
        t.profile?.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // 스타일 필터
    if (styleFilter !== '전체') {
      result = result.filter(t => t.profile?.trading_style === styleFilter);
    }
    
    // 리스크 필터
    if (riskFilter !== '전체') {
      result = result.filter(t => t.profile?.risk_tolerance === riskFilter);
    }

    // 정렬
    result.sort((a, b) => {
      switch(sortBy) {
        case 'name':
          return (a.profile?.name || '').localeCompare(b.profile?.name || '');
        case 'win_rate':
          return (b.performance?.win_rate || 0) - (a.performance?.win_rate || 0);
        case 'sharpe_ratio':
          return (b.performance?.sharpe_ratio || 0) - (a.performance?.sharpe_ratio || 0);
        case 'total_pnl':
          return (b.performance?.total_pnl || 0) - (a.performance?.total_pnl || 0);
        default:
          return 0;
      }
    });

    setFilteredTraders(result);
  };

  if (loading) return (
    <div className="text-center mt-5">
      <Spinner animation="border" variant="primary" />
    </div>
  );

  return (
    <div className="traders-page">
      <h2 className="mb-4">트레이더 목록</h2>
      
      <Card className="mb-3">
        <Card.Body>
          <Row className="g-3">
            <Col md={4}>
              <Form.Control
                type="text"
                placeholder="이름으로 검색..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Col>
            <Col md={3}>
              <Form.Select value={styleFilter} onChange={(e) => setStyleFilter(e.target.value)}>
                <option value="전체">모든 스타일</option>
                <option value="단기매매">단기매매</option>
                <option value="중기투자">중기투자</option>
                <option value="장기투자">장기투자</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Select value={riskFilter} onChange={(e) => setRiskFilter(e.target.value)}>
                <option value="전체">모든 리스크</option>
                <option value="저위험">저위험</option>
                <option value="중위험">중위험</option>
                <option value="고위험">고위험</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option value="name">이름순</option>
                <option value="win_rate">승률순</option>
                <option value="sharpe_ratio">샤프순</option>
                <option value="total_pnl">손익순</option>
              </Form.Select>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          {/* Desktop Table */}
          <div className="table-responsive desktop-only">
            <Table hover>
              <thead>
                <tr>
                  <th>이름</th>
                  <th>스타일</th>
                  <th>승률</th>
                  <th>샤프 비율</th>
                  <th>총 손익</th>
                  <th>거래 횟수</th>
                  <th>MDD</th>
                </tr>
              </thead>
              <tbody>
                {filteredTraders.map((trader, idx) => (
                  <tr 
                    key={idx} 
                    style={{cursor: 'pointer'}}
                    onClick={() => {
                      setSelectedTrader(trader);
                      setShowModal(true);
                    }}
                  >
                    <td>{trader.profile?.name || 'N/A'}</td>
                    <td>
                      <Badge bg="primary">{trader.profile?.trading_style || 'N/A'}</Badge>
                    </td>
                    <td>{(trader.performance?.win_rate || 0).toFixed(1)}%</td>
                    <td>{(trader.performance?.sharpe_ratio || 0).toFixed(2)}</td>
                    <td className={(trader.performance?.total_pnl || 0) > 0 ? 'text-success' : 'text-danger'}>
                      ${(trader.performance?.total_pnl || 0).toFixed(0)}
                    </td>
                    <td>{trader.performance?.total_trades || 0}</td>
                    <td className="text-danger">
                      {(trader.performance?.max_drawdown_pct || 0).toFixed(1)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          
          {/* Mobile Cards */}
          <div className="mobile-only">
            {filteredTraders.map((trader, idx) => (
              <div 
                key={idx} 
                className="trader-card"
                onClick={() => {
                  setSelectedTrader(trader);
                  setShowModal(true);
                }}
              >
                <div className="trader-card-header">
                  <h5>{trader.profile?.name || 'N/A'}</h5>
                  <Badge bg="primary">{trader.profile?.trading_style || 'N/A'}</Badge>
                </div>
                <div className="trader-card-body">
                  <div className="trader-stat">
                    <span className="label">승률</span>
                    <span className="value">{(trader.performance?.win_rate || 0).toFixed(1)}%</span>
                  </div>
                  <div className="trader-stat">
                    <span className="label">샤프 비율</span>
                    <span className="value">{(trader.performance?.sharpe_ratio || 0).toFixed(2)}</span>
                  </div>
                  <div className="trader-stat">
                    <span className="label">총 손익</span>
                    <span className={(trader.performance?.total_pnl || 0) > 0 ? 'value text-success' : 'value text-danger'}>
                      ${(trader.performance?.total_pnl || 0).toFixed(0)}
                    </span>
                  </div>
                  <div className="trader-stat">
                    <span className="label">거래 횟수</span>
                    <span className="value">{trader.performance?.total_trades || 0}회</span>
                  </div>
                  <div className="trader-stat">
                    <span className="label">MDD</span>
                    <span className="value text-danger">
                      {(trader.performance?.max_drawdown_pct || 0).toFixed(1)}%
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-muted mt-3">
            총 {filteredTraders.length}명의 트레이더
          </div>
        </Card.Body>
      </Card>
      
      {/* 상세 정보 모달 */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedTrader?.profile?.name || 'N/A'} 상세 정보
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedTrader && (
            <>
              <h5>프로필</h5>
              <Row className="mb-3">
                <Col md={6}>
                  <p><strong>입사일:</strong> {selectedTrader.profile?.join_date || 'N/A'}</p>
                  <p><strong>거래 스타일:</strong> {selectedTrader.profile?.trading_style || 'N/A'}</p>
                  <p><strong>리스크 성향:</strong> {selectedTrader.profile?.risk_tolerance || 'N/A'}</p>
                </Col>
                <Col md={6}>
                  <p><strong>경력:</strong> {selectedTrader.profile?.years_experience || 0}년</p>
                  <p><strong>선호 섹터:</strong> {selectedTrader.profile?.preferred_sectors || 'N/A'}</p>
                </Col>
              </Row>
              
              <h5>성과 지표</h5>
              <Row className="mb-3">
                <Col md={6}>
                  <p><strong>총 거래:</strong> {selectedTrader.performance?.total_trades || 0}회</p>
                  <p><strong>승률:</strong> {(selectedTrader.performance?.win_rate || 0).toFixed(1)}%</p>
                  <p><strong>샤프 비율:</strong> {(selectedTrader.performance?.sharpe_ratio || 0).toFixed(2)}</p>
                </Col>
                <Col md={6}>
                  <p><strong>총 손익:</strong> 
                    <span className={(selectedTrader.performance?.total_pnl || 0) > 0 ? 'text-success' : 'text-danger'}>
                      {' $'}{(selectedTrader.performance?.total_pnl || 0).toFixed(0)}
                    </span>
                  </p>
                  <p><strong>손익비:</strong> {(selectedTrader.performance?.profit_factor || 0).toFixed(2)}</p>
                  <p><strong>최대손실폭:</strong> 
                    <span className="text-danger">
                      {' '}{(selectedTrader.performance?.max_drawdown_pct || 0).toFixed(1)}%
                    </span>
                  </p>
                </Col>
              </Row>
              
              <h5>거래 패턴</h5>
              <p><strong>평균 보유기간:</strong> {(selectedTrader.pattern?.avg_hold_days || 0).toFixed(1)}일</p>
              <p><strong>가장 활발한 시간:</strong> {selectedTrader.pattern?.most_active_hour || 'N/A'}시</p>
              <p><strong>가장 활발한 요일:</strong> {selectedTrader.pattern?.most_active_day || 'N/A'}</p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            닫기
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TradersPage;
